"use client";

import React from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { ArrowRight, MapPin } from "lucide-react";

type Destination = { code: string; name: string; price?: string };

/** Market-test: popular destinations surfaced BEFORE How-it-Works
 *  (market-test-plan.md §3 `wf_destinations_before_howit_v1`). */
const DEST_EXPERIMENT_ID = "wf_destinations_before_howit_v1";

/**
 * Travel Connected — "Popular Destinations" PHOTO grid per
 * generated_homepage_or_landing.png: 4 ecommerce photo cards with the country
 * name + "From $X" overlaid at the bottom and a flag chip. Real destination
 * photography is an external launch asset; here each card uses the brand's
 * pc-photo-wash photographic placeholder + flag overlay (same commercial
 * rhythm). Server/presentational — destinations are passed in from real data.
 */
export default function DestinationsPhotoGrid({
  destinations,
}: {
  destinations: Destination[];
}) {
  const { t } = useTranslations();

  return (
    <section className="container mx-auto px-4 py-10">
      <Reveal as="div" className="flex items-end justify-between gap-4 mb-5" y={20}>
        <h2 className="text-2xl sm:text-3xl text-foreground">{t("pc.dest.title", "Popular destinations")}</h2>
        <Link href="/destinations/" className="inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:underline shrink-0 group/all">
          {t("pc.dest.viewAll", "View all countries")} <ArrowRight className="w-4 h-4 transition-transform group-hover/all:translate-x-0.5" />
        </Link>
      </Reveal>

      <Stagger as="div" className="grid grid-cols-2 lg:grid-cols-4 gap-4" staggerChildren={0.08} amount={0.2}>
        {destinations.slice(0, 4).map((d) => {
          const code = d.code.toLowerCase();
          return (
            <StaggerItem as="div" key={d.code} y={18}>
              <Link
                href={`/destinations/${code}/`}
                onClick={() => {
                  try {
                    track({
                      event_name: "destination_card_clicked",
                      event_id: `destination_card_clicked:${DEST_EXPERIMENT_ID}:${code}:${Date.now()}`,
                      experiment_id: DEST_EXPERIMENT_ID,
                      items: [{ item_id: code, item_name: d.name, country: d.name }],
                    });
                  } catch {
                    /* analytics is best-effort */
                  }
                }}
                className="group pc-photo pc-photo-scrim pc-photo-wash relative block aspect-[4/3] overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <span className="absolute inset-0 flex items-center justify-center text-white/30 transition-transform duration-[900ms] ease-out group-hover:scale-110">
                  <MapPin className="w-12 h-12" aria-hidden="true" />
                </span>
                {/* flag chip */}
                <img
                  src={`https://flagcdn.com/${code}.svg`}
                  alt=""
                  role="presentation"
                  width={32}
                  height={22}
                  className="absolute top-3 right-3 h-5 w-7 rounded shadow ring-1 ring-white/40"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
                {/* overlaid name + price */}
                <div className="absolute left-0 right-0 bottom-0 p-4 text-white">
                  <div className="font-bold text-lg leading-tight drop-shadow transition-transform duration-300 group-hover:-translate-y-0.5">{d.name}</div>
                  {d.price && (
                    <div className="text-sm font-semibold text-white/90 drop-shadow">
                      {t("destinations.fromPrice", "from {price}").replace("{price}", d.price)}
                    </div>
                  )}
                </div>
              </Link>
            </StaggerItem>
          );
        })}
      </Stagger>
    </section>
  );
}
